import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export const fieldInputClass = cn(
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white',
  'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
  'disabled:bg-gray-50 disabled:text-gray-400',
);

interface FieldProps {
  id: string;
  label: string;
  required?: boolean | undefined;
  internal?: boolean | undefined;
  hint?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/** A labeled field wrapper with optional "internal" marker and hint text. */
export function Field({
  id,
  label,
  required,
  internal,
  hint,
  children,
  className,
}: FieldProps) {
  return (
    <div className={className}>
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
        <span>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </span>
        {internal && <InternalBadge />}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

export function InternalBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
      title="Internal — not shown on storefront"
    >
      Internal
    </span>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  internal?: boolean;
  hint?: string;
  className?: string;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'url';
}

export function TextField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  maxLength,
  required,
  internal,
  hint,
  className,
  inputMode,
}: TextFieldProps) {
  return (
    <Field id={id} label={label} required={required} internal={internal} hint={hint} className={className}>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        aria-required={required || undefined}
        className={fieldInputClass}
      />
    </Field>
  );
}

interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  internal?: boolean;
  hint?: string;
  className?: string;
}

export function TextAreaField({
  id,
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
  maxLength,
  internal,
  hint,
  className,
}: TextAreaFieldProps) {
  return (
    <Field id={id} label={label} internal={internal} hint={hint} className={className}>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        className={fieldInputClass}
      />
    </Field>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  internal?: boolean;
  hint?: string;
  className?: string;
}

export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  required,
  internal,
  hint,
  className,
}: SelectFieldProps) {
  return (
    <Field id={id} label={label} required={required} internal={internal} hint={hint} className={className}>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={fieldInputClass}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

interface CheckboxFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  internal?: boolean;
  hint?: string;
}

export function CheckboxField({ id, label, checked, onChange, internal, hint }: CheckboxFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        />
        <span>{label}</span>
        {internal && <InternalBadge />}
      </label>
      {hint && <p className="mt-1 ml-6 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

/** A grouped fieldset card for a section of fields. */
export function SectionCard({
  title,
  description,
  internal,
  children,
}: {
  title: string;
  description?: string;
  internal?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
        {internal && <InternalBadge />}
      </div>
      {description && <p className="-mt-2 mb-4 text-xs text-gray-400">{description}</p>}
      <div className="space-y-4">{children}</div>
    </section>
  );
}
